import { isValidCalendarDate } from '@lib/datetime'
import { isInlineImageMime, isPdfMime } from '@lib/mime'
import { formatMoney } from '@lib/money'
import {
	buildDateRange,
	buildPreviewDays,
	isOnTripDay,
	MAX_PREVIEW_FILES,
	MAX_PREVIEW_IDEAS,
	MAX_PREVIEW_STAYS,
	OverviewPreviewEventInput,
	OverviewSegmentInput,
	OverviewTravelerInput,
	stayDateRange,
	sumInCurrency,
	toOverviewFlight,
	toTraveler,
} from './overviewShared'
import { OverviewFile, OverviewIdea, OverviewStay, TripOverviewData } from './types'

/**
 * The aggregated overview payload, in the shape both endpoints return it: the
 * member `GET trips/:tripId/overview` and the public `public-overview`, whose
 * sections additionally carry a `shared` flag this builder ignores. Structural,
 * so neither mode's generated wire type is imported here.
 */
export interface OverviewResponseLike {
	trip: { name: string; startDate: string | null; dayCount: number }
	travelers: readonly OverviewTravelerInput[]
	memberCount: number
	sections: {
		itinerary: {
			count: number
			events: readonly OverviewPreviewEventInput[]
			travelSegments: readonly OverviewSegmentInput[]
		}
		ideas: { count: number; ideas: readonly { id: number | string; name: string; thumbnailUrl: string | null }[] }
		accommodations: {
			count: number
			accommodations: readonly {
				id: number | string
				name: string
				city: string | null
				photoUrl: string | null
				checkInDate: string
				checkOutDate: string
			}[]
		}
		flights: {
			count: number
			firstFlight: {
				airlineIata: string
				flightNumber: string | null
				departureAirportCode: string | null
				arrivalAirportCode: string | null
				departureTimeStr: string
				durationMinutes: number | null
			} | null
		}
		files: {
			count: number
			files: readonly { id: string; fileName: string; mimeType: string; url: string | null }[]
		}
		expenses: { count: number; summary: { currencyCode: string; groupTotal: number } }
	}
}

/**
 * The two values the overview's modes resolve differently; everything else in
 * the payload maps identically, which is what keeps the member trip home and
 * the Shared trip view rendering the same tiles from the same numbers.
 */
export interface OverviewModeResolution {
	/** What the files tile advertises — see each caller for which total it means. */
	filesCount: number
	/** Pay ups behind the money tile's settled total; empty when withheld. */
	payUps: readonly { amount: number; currencyCode: string }[]
}

/** Normalize an aggregated overview payload into the bento's `TripOverviewData`. */
export const buildOverviewData = (
	response: OverviewResponseLike,
	{ filesCount, payUps }: OverviewModeResolution,
): TripOverviewData => {
	const { trip, travelers, memberCount, sections } = response
	const { startDate, dayCount } = trip
	const hasDates = isValidCalendarDate(startDate) && dayCount > 0
	const { currencyCode, groupTotal } = sections.expenses.summary

	return {
		hero: {
			name: trip.name,
			dayCount,
			hasDates,
			dateRange: buildDateRange(startDate, dayCount),
			travelers: travelers.map(toTraveler),
			memberCount,
		},
		itinerary: {
			hasDates,
			dayCount,
			// From the previewable slice, not the wire count: an out-of-range or
			// dateless payload must fall to `empty`, not render blank columns.
			eventCount: sections.itinerary.events.filter(event => isOnTripDay(event, dayCount)).length,
			previewDays: buildPreviewDays(
				startDate,
				dayCount,
				sections.itinerary.events,
				sections.itinerary.travelSegments,
			),
		},
		ideas: {
			count: sections.ideas.count,
			items: sections.ideas.ideas.slice(0, MAX_PREVIEW_IDEAS).map(
				(idea): OverviewIdea => ({
					id: String(idea.id),
					title: idea.name,
					thumbnailUrl: idea.thumbnailUrl ?? null,
				}),
			),
		},
		accommodations: {
			count: sections.accommodations.count,
			// The endpoint already returns these check-in ascending and capped.
			items: sections.accommodations.accommodations.slice(0, MAX_PREVIEW_STAYS).map(
				(stay): OverviewStay => ({
					id: String(stay.id),
					name: stay.name,
					city: stay.city ?? null,
					photoUrl: stay.photoUrl ?? null,
					dateRange: stayDateRange(stay.checkInDate, stay.checkOutDate),
				}),
			),
		},
		flights: {
			count: sections.flights.count,
			firstFlight: sections.flights.firstFlight
				? toOverviewFlight({
						...sections.flights.firstFlight,
						originCode: sections.flights.firstFlight.departureAirportCode,
						destinationCode: sections.flights.firstFlight.arrivalAirportCode,
						// Both endpoints expose the first flight as a wall-clock string.
						departureTime: null,
					})
				: null,
		},
		files: {
			count: filesCount,
			items: sections.files.files.slice(0, MAX_PREVIEW_FILES).map(
				(file): OverviewFile => ({
					id: file.id,
					fileName: file.fileName,
					url: file.url ?? null,
					// Non-decodable types (HEIC, PDFs) fall back to the icon chip.
					thumbnailUrl: isInlineImageMime(file.mimeType) ? (file.url ?? null) : null,
					isPdf: isPdfMime(file.mimeType),
				}),
			),
		},
		money: {
			expenseCount: sections.expenses.count,
			hasExpenses: sections.expenses.count > 0,
			currencyCode,
			expenseTotal: formatMoney(groupTotal, currencyCode),
			payUpCount: payUps.length,
			payUpTotal: formatMoney(sumInCurrency(payUps, currencyCode), currencyCode),
		},
	}
}
